"use client";

import { useState } from "react";
import { I } from "@/components/kamibot-v2/discord";

type Cycle = "month" | "year";
type CtaKind = "primary" | "ghost" | "ghost-strong";

type Plan = {
  id: "free" | "pro" | "studio";
  name: string;
  tag: string;
  tagline: string;
  price: { month: number; year: number };
  cta: string;
  ctaKind: CtaKind;
  pitch: string;
  highlight?: boolean;
  feats: { txt: string; inc: boolean; hi?: boolean }[];
};

const PLANS: Plan[] = [
  {
    id: "free",
    name: "무료",
    tag: "FREE",
    tagline: "지금 이대로 충분해요",
    price: { month: 0, year: 0 },
    cta: "지금 쓰는 그대로",
    ctaKind: "ghost",
    pitch: "카드 등록도, 회원가입도 없어요. 카미봇의 거의 모든 기능을 그대로.",
    feats: [
      { txt: "기본 TTS · 30초까지", inc: true },
      { txt: "AI 어시스턴트 · 일일 50회", inc: true },
      { txt: "자동 번역 · 108개 언어", inc: true },
      { txt: "환영 카드 · 임시 음성 채널", inc: true },
      { txt: "음악 재생 · 표준 음질", inc: true },
      { txt: "지원 서버 응대", inc: true },
    ],
  },
  {
    id: "pro",
    name: "프로",
    tag: "PRO",
    tagline: "개인용 · 한 잔 사주는 마음",
    price: { month: 4900, year: 49000 },
    cta: "프로 시작하기",
    ctaKind: "primary",
    pitch: "리소스가 실제로 비싼 기능 — 긴 음성, 고품질 음악, 무제한 AI — 를 풀어요.",
    highlight: true,
    feats: [
      { txt: "긴 TTS · 5분까지 · 고급 음성", inc: true, hi: true },
      { txt: "AI 어시스턴트 · 무제한", inc: true, hi: true },
      { txt: "음악 · 고품질 음원 · 우선 큐", inc: true, hi: true },
      { txt: "AI 자동화 · 모더레이션 · 요약", inc: true, hi: true },
      { txt: "서포터 ✦ 마크 (디스코드 역할)", inc: true },
      { txt: "서포터 전용 공지 · 비하인드", inc: true },
      { txt: "카미봇이 보내는 감사 메시지", inc: true },
    ],
  },
  {
    id: "studio",
    name: "스튜디오",
    tag: "STUDIO",
    tagline: "우리 서버만의 커스텀 봇",
    price: { month: 19900, year: 199000 },
    cta: "스튜디오 알아보기",
    ctaKind: "ghost-strong",
    pitch: "카미봇 그대로, 우리 서버 색으로. 이름·아바타·임베드 색까지 다 바꿔요.",
    feats: [
      { txt: "프로의 모든 기능", inc: true },
      { txt: "커스텀 봇 (이름·프로필 사진·소개)", inc: true, hi: true },
      { txt: "임베드 색 · 환영 멘트 · 응답 톤", inc: true, hi: true },
      { txt: "내 봇 토큰으로 직접 만들기", inc: true, hi: true },
      { txt: "서버 분석 대시보드", inc: true },
      { txt: "우선 지원 (24시간 내 응답)", inc: true },
      { txt: "카미봇이 직접 쓰는 손편지 (연 1회)", inc: true },
    ],
  },
];

function PriceFmt({ amount, cycle }: { amount: number; cycle: Cycle }) {
  if (amount === 0) return <span className="prem-price-num">₩0</span>;
  return (
    <>
      <span className="prem-price-num">₩{amount.toLocaleString()}</span>
      <span className="prem-price-unit">/{cycle === "month" ? "월" : "년"}</span>
    </>
  );
}

export function PremiumPlans() {
  const [cycle, setCycle] = useState<Cycle>("year");
  const yearSavingsPct = 17;

  const handlePlanCta = (planId: Plan["id"]) => {
    // TODO: 결제 연동 (planId, cycle 기반으로 결제 플로우)
    void planId;
    alert("곧 출시됩니다!");
  };

  return (
    <section className="prem-plans">
      <div className="shell">
        <div className="section-head">
          <span className="section-eyebrow">
            <I.sparkle /> PLANS · 플랜
          </span>
          <h2 className="section-title">세 가지 마음 — 그래도, 무료가 기본이에요.</h2>
          <p className="section-sub">
            <b>무료</b>로도 카미봇은 충분히 잘 작동해요.
            <br />
            <b>프로</b>는 자원이 많이 드는 기능들을 풀어드리고, <b>스튜디오</b>는 서버 전용 봇으로 변신시켜요.
          </p>
        </div>

        <div className="prem-billing-toggle">
          <button
            type="button"
            className={`prem-billing-opt ${cycle === "month" ? "active" : ""}`}
            onClick={() => setCycle("month")}
          >
            월간
          </button>
          <button
            type="button"
            className={`prem-billing-opt ${cycle === "year" ? "active" : ""}`}
            onClick={() => setCycle("year")}
          >
            연간
            <span className="prem-billing-save">2개월 아낌 · −{yearSavingsPct}%</span>
          </button>
        </div>

        <div className="prem-plan-grid">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className={`prem-plan ${p.highlight ? "is-highlight" : ""}`}
            >
              {p.highlight && <span className="prem-plan-recommend">추천</span>}
              <div className="prem-plan-head">
                <span className="prem-plan-tag">{p.tag}</span>
                <h3 className="prem-plan-name">{p.name}</h3>
                <p className="prem-plan-tagline gowun">{p.tagline}</p>
              </div>

              <div className="prem-plan-price">
                <PriceFmt amount={p.price[cycle]} cycle={cycle} />
                {cycle === "year" && p.price.year > 0 && (
                  <span className="prem-plan-price-sub">
                    월 ₩{Math.round(p.price.year / 12).toLocaleString()} 꼴
                  </span>
                )}
                {p.price.month === 0 && (
                  <span className="prem-plan-price-sub">영원히 0원</span>
                )}
              </div>

              <p className="prem-plan-pitch">{p.pitch}</p>

              <ul className="prem-plan-feats">
                {p.feats.map((f, j) => (
                  <li key={j} className={f.hi ? "is-hi" : ""}>
                    <span className="feat-ic">
                      <I.check />
                    </span>
                    <span>{f.txt}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`btn ${
                  p.ctaKind === "primary"
                    ? "btn-primary"
                    : p.ctaKind === "ghost-strong"
                    ? "btn-ghost-strong"
                    : "btn-ghost"
                }`}
                onClick={() => handlePlanCta(p.id)}
              >
                {p.cta}
                {p.ctaKind === "primary" && <I.arrow />}
              </button>

              {p.id !== "free" && (
                <span className="prem-plan-foot">14일 환불 · 언제든 해지</span>
              )}
            </div>
          ))}
        </div>

        <div className="prem-plans-tiny gowun">
          미안하지만 — 카드 정보를 받는 건 결제 시점에만이에요. 미리 등록해두지 않아요.
        </div>
      </div>
    </section>
  );
}
